import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useEditGate } from '../../components/confirm';
import { AppToolbar, EditGateActions } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import {
  formatCombo, comboFromEvent, parseCombo, setHotkeyOverrides,
} from '../../components/hotkeys';
import '../backlog/backlog.css';

// ─────────────────────────────────────────────────────────────────────────────
// Settings → Keyboard Shortcuts  (ADR-0013 Phase 3 part 2 — per-tenant key remap)
//
// Lets an admin re-key the reserved-policy actions app-wide. Overrides are stored
// per tenant in dbo.AI_AppSettings key `Hotkeys` (GET/PUT /api/v1/app-config,
// audited "App Config"), applied after login by AppShell → setHotkeyOverrides, and
// consulted by the shared hotkey store's matcher + the ? sheet / Ctrl+K palette.
// Only the semantic reserved actions are remappable; the ? sheet stays the live
// source of truth for what each key currently does.
// ─────────────────────────────────────────────────────────────────────────────

/** The reserved-policy actions a tenant may re-key. Each `combo` is the DEFAULT;
 *  an override re-keys that semantic action everywhere at once. */
const ACTIONS: ReadonlyArray<{ combo: string; label: string }> = [
  { combo: 'alt+n', label: 'New' },
  { combo: 'alt+e', label: 'Edit' },
  { combo: 'alt+d', label: 'Delete' },
  { combo: 'alt+p', label: 'Preview' },
  { combo: 'alt+r', label: 'Refresh' },
  { combo: 'alt+i', label: 'Import' },
  { combo: 'alt+x', label: 'Export' },
  { combo: 'mod+s', label: 'Save' },
  { combo: 'mod+shift+s', label: 'Save & New' },
  { combo: 'mod+enter', label: 'Save & Close' },
  { combo: 'mod+k', label: 'Command palette' },
];

/** Combos the browser owns — can't be reliably intercepted, so never allowed. */
const RESERVED_BROWSER = new Set(['mod+n', 'mod+t', 'mod+w', 'mod+r', 'mod+tab', 'mod+shift+n', 'mod+shift+t']);

const DEFAULTS = new Set(ACTIONS.map((a) => a.combo));

export default function KeyboardShortcutsPage() {
  const notify = useNotify();
  const gate = useEditGate();
  const ro = gate.readOnly;

  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState<Record<string, string>>({});
  const [capturing, setCapturing] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const res = await api.get<{ success: boolean; data: { hotkeys?: Record<string, string> } }>(
        '/api/v1/app-config', { silentLoading: true } as never);
      const map = res.data?.data?.hotkeys ?? {};
      // Keep only overrides for actions this screen manages.
      const mine: Record<string, string> = {};
      for (const [k, v] of Object.entries(map)) if (DEFAULTS.has(k) && v) mine[k] = v;
      setOverrides(mine);
      setLoaded(mine);
    } catch { notify.error('Could not load keyboard shortcuts.'); }
    finally { setLoadMs(performance.now() - t0); setLoading(false); }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  const effective = (def: string) => overrides[def] ?? def;

  /** null = valid; otherwise the reason to reject the captured combo. */
  const validate = (combo: string, def: string): string | null => {
    const p = parseCombo(combo);
    if (!(p.mod || p.ctrl || p.alt)) return 'Use a shortcut with Ctrl, Alt or Cmd.';
    if (RESERVED_BROWSER.has(combo)) return 'That shortcut is reserved by the browser.';
    for (const a of ACTIONS) {
      if (a.combo === def) continue;
      if ((overrides[a.combo] ?? a.combo) === combo) return `Already used by "${a.label}".`;
    }
    return null;
  };

  const onCaptureKey = (e: React.KeyboardEvent, def: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.key === 'Escape') { setCapturing(null); return; }
    const combo = comboFromEvent(e.nativeEvent);
    if (!combo) return; // lone modifier — keep listening
    const err = validate(combo, def);
    if (err) { notify.warning(err); return; }
    setOverrides((prev) => {
      const next = { ...prev };
      if (combo === def) delete next[def]; else next[def] = combo;
      return next;
    });
    setCapturing(null);
  };

  const resetOne = (def: string) =>
    setOverrides((prev) => { const n = { ...prev }; delete n[def]; return n; });

  // gate.cancel(revert) so Cancel also leaves edit mode (owner report 2026-10-12).
  const handleCancel = () => gate.cancel(() => { setOverrides(loaded); setCapturing(null); });

  const handleSave = () => {
    setSaving(true);
    void gate.save({
      formName: 'App Config',
      successMessage: 'Keyboard shortcuts saved successfully.',
      save: async () => {
        await api.put('/api/v1/app-config/hotkeys', { hotkeys: overrides });
        setHotkeyOverrides(overrides); // take effect live, no reload
      },
      reload: load,
      onError: (err: unknown) => {
        const status = (err as { response?: { status?: number } })?.response?.status;
        notify.error(status === 403
          ? 'Administrator / service access required to change keyboard shortcuts.'
          : 'Save failed. Please try again.');
      },
    }).finally(() => setSaving(false));
  };

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Settings</span><i className="ti ti-chevron-right" />
            {/* subgroup node (owner request 2026-10-14) */}
            <span>Default Settings</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Keyboard Shortcuts</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="hotkeysconfig" auditFormName="App Config"
              loadMs={loadMs} loading={loading} />
          </div>
          <div className="dp-subtitle">
            <i className="ti ti-info-circle" style={{ fontSize: 13, verticalAlign: 'middle', marginRight: 4 }} />
            Re-key the standard actions for this tenant. Applies everywhere at once; press
            <kbd style={{ margin: '0 4px' }}>?</kbd> on any form to see the keys in force.
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={<EditGateActions gate={gate} saving={saving} disabled={loading}
              onSave={handleSave} onCancel={handleCancel} />}
            onRefresh={() => void load()} refreshing={loading} refreshDisabled={gate.editing}
          />
        </div>

        <div className="bl-card bl-card-flex">
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '18px 28px 32px' }}>
            <div style={{ maxWidth: 640 }}>
              {ACTIONS.map((a) => {
                const eff = effective(a.combo);
                const isOverridden = eff !== a.combo;
                return (
                  <div key={a.combo} style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '8px 0', borderBottom: '1px solid var(--ai-line-soft, #eef0f3)',
                  }}>
                    <div style={{ minWidth: 150, fontSize: 13, fontWeight: 600, color: 'var(--ai-ink)' }}>
                      {a.label}
                    </div>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
                      {capturing === a.combo ? (
                        <input
                          autoFocus
                          readOnly
                          className="hk-kbd"
                          style={{ minWidth: 150, padding: '4px 8px', outline: '2px solid var(--brand-accent, #e8670a)' }}
                          value="Press keys…  (Esc to cancel)"
                          onKeyDown={(e) => onCaptureKey(e, a.combo)}
                          aria-label={`Capture a new shortcut for ${a.label}`}
                        />
                      ) : (
                        <kbd className="hk-kbd" style={{ minWidth: 60, textAlign: 'center' }}>{formatCombo(eff)}</kbd>
                      )}
                      {isOverridden && (
                        <span style={{ fontSize: 11, color: 'var(--ai-muted)' }}>
                          default {formatCombo(a.combo)}
                        </span>
                      )}
                      {!ro && capturing !== a.combo && (
                        <>
                          <button className="ca-btn" style={{ padding: '3px 10px' }}
                            onClick={() => setCapturing(a.combo)} title={`Set a new shortcut for ${a.label}`}>
                            Set…
                          </button>
                          {isOverridden && (
                            <button className="ca-btn" style={{ padding: '3px 10px' }}
                              onClick={() => resetOne(a.combo)} title="Reset to default">
                              ↺ Reset
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
